import { ApiDeliveryAddress, ApiList, ApiSchedule, ScheduleStatus } from '../../types';

/* naming is step one and is over before the wizard opens, so the modals carry two, three and four */
export const WIZARD_STEPS = 4;

export type WizardStep = 'address' | 'schedule' | 'products';

export const stepNumber: Record<WizardStep, number> = { address: 2, schedule: 3, products: 4 };

/*
 * An api that has not been released with the column answers without one, and every schedule it
 * answers with is one it is already creating orders from.
 */
export const statusOf = (schedule?: ApiSchedule): ScheduleStatus => schedule?.status ?? 'active';

export const isDraft = (schedule?: ApiSchedule): boolean => !schedule || statusOf(schedule) === 'draft';

/* a draft is ready to be activated once the three things an order needs are in place */
export const canActivate = (list: ApiList, schedule?: ApiSchedule): boolean =>
	Boolean(schedule) && whatToDoNext(list, schedule) === null;

/*
 * The address on a schedule, if it really has one. 0 is the api's way of saying it has none, and
 * `ApiDeliveryAddress.id` is a string as readily as a number - `productListSchedule.json.twig`
 * nulls the address out for both today, and this does not depend on it still doing that.
 */
export const deliveryAddressOf = (schedule?: ApiSchedule): ApiDeliveryAddress | undefined =>
	schedule?.deliveryAddress && Number(schedule.deliveryAddress.id) ? schedule.deliveryAddress : undefined;

/* what a subscription needs before anything is sent, in the order the wizard asks for it */
export const missingSteps = (list: ApiList, schedule?: ApiSchedule): WizardStep[] => {
	const steps: WizardStep[] = [];

	// the delivery address is a column on the schedule, so a list without a schedule lacks both
	if (!deliveryAddressOf(schedule)) steps.push('address');
	if (!schedule) steps.push('schedule');
	if (list.items.length === 0) steps.push('products');

	return steps;
};

export const isIncomplete = (list: ApiList, schedule?: ApiSchedule): boolean => missingSteps(list, schedule).length > 0;

/*
 * What the card asks the customer to do next, which is not the order the wizard asks in: the wizard
 * takes the address first and carries it to the schedule, while the card can only point at the
 * schedule, since that is where an address would be kept.
 */
export const whatToDoNext = (list: ApiList, schedule?: ApiSchedule): WizardStep | null => {
	if (!schedule) return 'schedule';
	if (!deliveryAddressOf(schedule)) return 'address';
	if (!list.items.length) return 'products';

	return null;
};

/*
 * What the modal standing in for a step needs to know: which step it is, and the two ways out of it.
 * Being handed one is also what tells a modal to open - the wizard takes the prop away as it moves on.
 */
export type WizardControl = {
	/* which of the four this is, or nothing for a step opened on its own from the draft card */
	step?: number;
	total: number;
	/* whether the step has what it needs; a step that saves as it goes says when it is done enough */
	ready: boolean;
	/* the step is done; the wizard moves on and this modal closes with it */
	onDone: () => void;
	/* closed without finishing; what is already saved stays, and the card offers to carry on later */
	onAbandon: () => void;
};
